// Audio Announcement Utility
// Handles audio playback with fallback to TTS based on voice mode selection

// Voice modes
export const VOICE_MODES = {
  DEFAULT: 'default',           // Uses Google TTS
  NGUYEN_NGOC_NGAN: 'NguyenNgocNgan'  // Uses audio files
};

// Audio file mapping for NguyenNgocNgan voice
const AUDIO_FILE_MAP = {
  'phase_werewolf': '/audio/announcements/NguyenNgocNgan/phase_werewolf.mp3',
  'phase_doctor': '/audio/announcements/NguyenNgocNgan/phase_doctor.mp3',
  'phase_seer': '/audio/announcements/NguyenNgocNgan/phase_seer.mp3',
  'phase_hunter_aim': '/audio/announcements/NguyenNgocNgan/phase_hunter_aim.mp3',
  'phase_hunter_revenge': '/audio/announcements/NguyenNgocNgan/phase_hunter_revenge.mp3',
  'phase_witch_save': '/audio/announcements/NguyenNgocNgan/phase_witch_save.mp3',
  'phase_witch_kill': '/audio/announcements/NguyenNgocNgan/phase_witch_kill.mp3',
  'phase_voting_accuse_0_deaths': '/audio/announcements/NguyenNgocNgan/phase_voting_accuse_0_deaths.mp3',
  'phase_voting_accuse_1_death': '/audio/announcements/NguyenNgocNgan/phase_voting_accuse_1_deaths.mp3', // Note: file is plural "deaths"
  'phase_voting_accuse_2_deaths': '/audio/announcements/NguyenNgocNgan/phase_voting_accuse_2_deaths.mp3',
  'phase_voting_accuse_3_deaths': '/audio/announcements/NguyenNgocNgan/phase_voting_accuse_3_deaths.mp3',
  'phase_defense': '/audio/announcements/NguyenNgocNgan/phase_defense.mp3',
  'phase_voting_decision': '/audio/announcements/NguyenNgocNgan/phase_voting_decision.mp3',
  'vote_tie_accuse_rounds_1_3': '/audio/announcements/NguyenNgocNgan/vote_tie_accuse_rounds_1_3.mp3',
  'vote_insufficient_rounds_1_3': '/audio/announcements/NguyenNgocNgan/vote_insufficient_rounds_1_3.mp3',
  'vote_tie_accuse_round_4_plus': '/audio/announcements/NguyenNgocNgan/vote_tie_accuse_round_4_plus.mp3',
  'vote_tie_decision': '/audio/announcements/NguyenNgocNgan/vote_tie_decision.mp3',
  'vote_result_killed': '/audio/announcements/NguyenNgocNgan/vote_result_killed.mp3',
  'vote_result_spared': '/audio/announcements/NguyenNgocNgan/vote_result_spared.mp3',
  'general_game_continues': '/audio/announcements/NguyenNgocNgan/general_game_continues.mp3',
  'winner_werewolf': '/audio/announcements/NguyenNgocNgan/winner_werewolf.mp3',
  'winner_villager': '/audio/announcements/NguyenNgocNgan/winner_villager.mp3',
};

// Get current voice mode from localStorage
export function getVoiceMode() {
  return localStorage.getItem('adminVoiceMode') || VOICE_MODES.DEFAULT;
}

// Set voice mode in localStorage
export function setVoiceMode(mode) {
  localStorage.setItem('adminVoiceMode', mode);
}

// Play TTS announcement (helper function)
function playTTSAnnouncement(messageText, options = {}) {
  if (!('speechSynthesis' in window)) {
    console.warn('[audioAnnouncements] Speech synthesis not available');
    return null;
  }

  // Cancel any ongoing speech
  try {
    window.speechSynthesis.cancel();
  } catch (e) {
    // Ignore cancel errors
  }

  const utterance = new SpeechSynthesisUtterance(messageText);
  utterance.lang = options.lang || 'vi-VN'; // Vietnamese
  utterance.rate = options.rate || 0.9; // Slightly slower for clarity
  utterance.pitch = options.pitch || 1;
  utterance.volume = options.volume || 1;

  // Try to use a Google voice if available
  const voices = window.speechSynthesis.getVoices();
  const googleVoice = voices.find(voice => 
    voice.name.toLowerCase().includes('google') && 
    (voice.lang.startsWith('vi') || voice.lang === 'vi-VN')
  );
  if (googleVoice) {
    utterance.voice = googleVoice;
  } else {
    // Fallback to any Vietnamese voice
    const viVoice = voices.find(voice => voice.lang.startsWith('vi'));
    if (viVoice) {
      utterance.voice = viVoice;
    }
  }

  // Set up error handler
  if (options.onerror) {
    utterance.onerror = options.onerror;
  }

  if (options.onstart) {
    utterance.onstart = options.onstart;
  }

  if (options.onend) {
    utterance.onend = options.onend;
  }

  try {
    window.speechSynthesis.speak(utterance);
    return utterance;
  } catch (error) {
    console.error('[audioAnnouncements] TTS playback failed:', error);
    return null;
  }
}

// Play announcement (audio or TTS based on mode)
export function playAnnouncement(messageKey, messageText, options = {}) {
  const voiceMode = getVoiceMode();
  const fallbackToTTS = options.fallbackToTTS !== false; // Default to true
  
  console.log(`[audioAnnouncements] playAnnouncement called - messageKey: ${messageKey}, voiceMode: ${voiceMode}, VOICE_MODES.DEFAULT: ${VOICE_MODES.DEFAULT}, VOICE_MODES.NGUYEN_NGOC_NGAN: ${VOICE_MODES.NGUYEN_NGOC_NGAN}`);
  
  // If default mode, use TTS
  if (voiceMode === VOICE_MODES.DEFAULT) {
    console.log(`[audioAnnouncements] Using TTS (default mode) for: ${messageKey}`);
    return playTTSAnnouncement(messageText, options);
  }
  
  // Otherwise, try to play audio file
  const audioPath = AUDIO_FILE_MAP[messageKey];
  if (!audioPath) {
    console.warn(`[audioAnnouncements] No audio file mapping found for: ${messageKey}`);
    if (fallbackToTTS) {
      console.log(`[audioAnnouncements] Falling back to TTS (no mapping)`);
      return playTTSAnnouncement(messageText, options);
    }
    return null;
  }

  console.log(`[audioAnnouncements] Attempting to play audio file: ${audioPath}`);
  const audio = new Audio(audioPath);
  
  // Set up error handler
  audio.onerror = (error) => {
    console.error(`[audioAnnouncements] ❌ Failed to load audio: ${audioPath}`, error);
    console.error(`[audioAnnouncements] Error details:`, {
      error,
      code: audio.error?.code,
      message: audio.error?.message,
      networkState: audio.networkState,
      readyState: audio.readyState
    });
    if (fallbackToTTS) {
      console.log(`[audioAnnouncements] Falling back to TTS (audio load failed)`);
      playTTSAnnouncement(messageText, options);
    }
  };
  
  // Also listen for load errors
  audio.addEventListener('error', (e) => {
    console.error(`[audioAnnouncements] Audio error event:`, e);
  });

  // Set up event handlers
  if (options.onerror) {
    audio.addEventListener('error', (error) => {
      options.onerror(error);
      if (fallbackToTTS) {
        playTTSAnnouncement(messageText, options);
      }
    });
  }

  if (options.onstart) {
    audio.addEventListener('play', options.onstart);
  }

  if (options.onend) {
    audio.addEventListener('ended', options.onend);
  }
  
  // Set up success handler
  audio.addEventListener('loadeddata', () => {
    console.log(`[audioAnnouncements] ✅ Audio file loaded successfully: ${audioPath}`);
  });
  
  audio.addEventListener('canplay', () => {
    console.log(`[audioAnnouncements] ✅ Audio can play: ${audioPath}`);
  });
  
  // Play audio
  audio.play().then(() => {
    console.log(`[audioAnnouncements] ✅ Audio playback started: ${audioPath}`);
  }).catch(error => {
    console.error('[audioAnnouncements] ❌ Audio playback failed:', error);
    console.error('[audioAnnouncements] Playback error details:', {
      error,
      code: audio.error?.code,
      message: audio.error?.message,
      networkState: audio.networkState,
      readyState: audio.readyState,
      src: audio.src
    });
    if (fallbackToTTS) {
      console.log(`[audioAnnouncements] Falling back to TTS (playback failed)`);
      playTTSAnnouncement(messageText, options);
    }
  });
  
  return audio; // Return audio instance for control (pause, cancel, etc.)
}

// Cancel announcement (audio or TTS)
export function cancelAnnouncement(audioInstance) {
  if (audioInstance) {
    // Check if it's an Audio instance or SpeechSynthesisUtterance
    if (audioInstance.pause && typeof audioInstance.pause === 'function') {
      // It's an Audio instance
      audioInstance.pause();
      audioInstance.currentTime = 0;
    }
  }
  // Also cancel any TTS that might be playing
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}

